import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Mail, Users2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const statusTone = {
  active: "border-green-200 bg-green-50 text-green-800",
  payment_required: "border-amber-200 bg-amber-50 text-amber-900",
  past_due: "border-red-200 bg-red-50 text-red-800",
  canceled: "border-slate-200 bg-slate-50 text-slate-700",
};

export default function Subscription() {
  const { t } = useTranslation();
  const { user, company: authCompany } = useAuth();
  const [plans, setPlans] = useState([]);
  const [contact, setContact] = useState(null);
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([api.get("/plans"), api.get("/company")]);
    setPlans(p.data.plans || []);
    setContact({ note: p.data.contact_note, email: p.data.contact_email, subject: p.data.contact_subject });
    setCompany(c.data);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, [load]);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy("");
    }
  };

  const cancel = async () => {
    if (!window.confirm(t("sub.cancelConfirm"))) return;
    try {
      const { data } = await api.post("/company/cancel");
      setCompany(data);
      toast.success(t("sub.cancelDone"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const isAdmin = (user?.role === "company_admin" || user?.role === "superadmin") && !authCompany?.demo_readonly;

  return (
    <div data-testid="subscription-page">
      <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("sub.title")}</h1>
      <p className="text-sm text-slate-500">{t("sub.subtitle")}</p>

      {company && (
        <div
          data-testid="subscription-status"
          className={`mt-4 rounded-lg border p-4 ${
            company.trial_status === "trial_active"
              ? "border-blue-200 bg-blue-50 text-blue-900"
              : statusTone[company.status] || statusTone.payment_required
          }`}
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-base font-semibold" data-testid="subscription-company-name">{company.name}</span>
            <span className="text-sm font-medium" data-testid="subscription-plan">
              {company.plan_name || t("sub.noPlan")}
            </span>
            <span className="text-sm font-medium uppercase tracking-[0.05em]" data-testid="subscription-state">
              {company.trial_status === "trial_active" && !company.plan
                ? "Provperiod aktiv"
                : t(`sub.status.${company.status}`)}
            </span>
            {company.trial_status === "trial_active" && company.trial_days_left != null && (
              <span className="rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800" data-testid="subscription-trial">
                Provperiod: {company.trial_days_left} dagar kvar
              </span>
            )}
            {company.trial_status === "trial_expired" && (
              <span className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-800" data-testid="subscription-trial-expired">
                Provperioden är slut
              </span>
            )}
            {company.free_access && (
              <span className="rounded border border-teal-300 bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-800" data-testid="subscription-free-access">
                {t("sub.freeAccess")}
              </span>
            )}
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-sm" data-testid="subscription-users">
            <Users2 className="h-3.5 w-3.5" />
            {company.max_users
              ? t("sub.usersOf", { used: company.users, max: company.max_users })
              : `${company.users} användare – inget användartak under provperioden`}
          </p>
          {company.trial_status !== "trial_active" && !company.plan && !company.has_subscription && (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="subscription-no-trial-note">
              Ditt företag har redan använt den kostnadsfria provperioden på 60 dagar. Välj ett
              abonnemang nedan för att komma igång – all din data ligger kvar.
            </p>
          )}
          {company.current_period_end && (
            <p className="mt-1 text-sm" data-testid="subscription-period-end">
              {company.cancel_at_period_end ? t("sub.endsAt") : t("sub.renewsAt")}{" "}
              {company.current_period_end.slice(0, 10)}
            </p>
          )}
        </div>
      )}

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="plans-grid">
        {plans.map((p) => {
          const current = company?.plan === p.key;
          return (
            <div
              key={p.key}
              data-testid={`plan-card-${p.key}`}
              className={`flex flex-col rounded-lg border bg-white p-4 transition-colors duration-200 ${
                current ? "border-blue-500 ring-2 ring-blue-100" : "border-slate-200 hover:border-blue-300"
              }`}
            >
              <h2 className="text-lg font-semibold text-slate-900">{p.name}</h2>
              <p className="mt-1 font-heading text-3xl font-bold text-slate-900" data-testid={`plan-price-${p.key}`}>
                {p.price.toLocaleString("sv-SE")} <span className="text-base font-medium text-slate-500">kr/mån</span>
              </p>
              <p className="text-xs text-slate-500">{t("sub.exclVat")}</p>
              <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <span data-testid={`plan-users-${p.key}`}>{p.description}</span>
              </p>
              {isAdmin && (
                <Button
                  data-testid={`plan-select-${p.key}`}
                  disabled={!!busy}
                  onClick={() => checkout(p.lookup_key)}
                  className="mt-4 h-11 w-full bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
                >
                  {busy === p.lookup_key ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : current ? (
                    t("sub.currentPlan")
                  ) : (
                    t("sub.choosePlan")
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {contact?.email && (
        <div
          className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
          data-testid="plans-contact-card"
        >
          <p className="flex-1 text-base font-semibold text-slate-800" data-testid="plans-contact-note">
            {contact.note}
          </p>
          <a
            href={`mailto:${contact.email}?subject=${encodeURIComponent(contact.subject || "")}`}
            data-testid="plans-contact-link"
            className="inline-flex h-10 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-slate-700"
          >
            <Mail className="h-4 w-4" /> {contact.email}
          </a>
        </div>
      )}

      {isAdmin && company?.stripe_subscription_id !== null && company?.status === "active" && !company?.cancel_at_period_end && (
        <Button
          data-testid="subscription-cancel"
          variant="outline"
          onClick={cancel}
          className="mt-5 h-10 border-red-200 bg-white text-red-600 transition-colors duration-200 hover:bg-red-50"
        >
          {t("sub.cancel")}
        </Button>
      )}

      <p className="mt-4 text-xs text-slate-500">{t("sub.dataNote")}</p>
    </div>
  );
}
